import type { Event, ScheduleCard } from '@pa/core';
import { deviceZone } from './format';
import { useCallback, useEffect, useRef, useState } from 'react';
import { wsUrl } from './api';

/** Mirrors apps/api/src/protocol.ts. */
export type AgentTurnState = 'thinking' | 'working' | 'waiting' | 'done';
export type StepStatus = 'running' | 'done' | 'failed';

export interface StepFrame {
  type: 'step';
  trace_id: string;
  step_id: string;
  label: string;
  status: StepStatus;
}

/** Which button the approval card shows (i18n key under approval.actions). */
export type ApprovalActionLabel = 'approve_update' | 'approve_create' | 'approve_send' | 'approve';

/**
 * Approval request: one concrete action TWO proposes. Everything the person needs to decide
 * is on the card; the signature binds approval_id to the task, the person and the expiry.
 */
export interface ApprovalFrame {
  type: 'approval';
  trace_id: string;
  approval_id: string;
  task_id: string;
  /** calendar_update | calendar_create | mail_send | mail_draft */
  kind: string;
  /** What is being approved, e.g. "Meeting with Alex → Thursday 15:00–16:00". */
  item: string;
  route?: string;
  /** Dates and times with the zone, e.g. "Thu 25 Sep, 15:00–16:00 (Asia/Qatar)". */
  dates?: string;
  timezone?: string;
  attendees?: string[];
  recipients?: string[];
  subject?: string;
  /** Exact message preview for mail. */
  preview?: string;
  /** Fare or service class (purchases, later). */
  class?: string;
  amount?: string;
  currency?: string;
  payment_method_last4?: string;
  cancellation_terms?: string;
  /** 4–5 word reason from the model's structured output. */
  why?: string;
  /** From the permission engine, already in the person's language. */
  authority?: string;
  action_label: ApprovalActionLabel;
  expires_at: string;
  signature: string;
}

/** Outcome of an approval, after the person decided (or the card expired). */
export interface ApprovalResultFrame {
  type: 'approval_result';
  trace_id: string;
  approval_id: string;
  task_id: string;
  status: 'approved' | 'cancelled' | 'failed' | 'expired';
  /** Done-state checklist, in the person's language ("Event updated", "Update sent to …"). */
  checklist: string[];
}

/** Server-side timings of one turn in ms (mirrors apps/api/src/protocol.ts TurnTimings). */
export interface TurnTimings {
  first_token?: number;
  model?: number;
  transcribe?: number;
  read?: number;
  memory?: number;
  total: number;
}

export type ServerErrorCode =
  | 'agent_failed'
  | 'bad_message'
  | 'binary_unsupported'
  | 'attachment_not_found'
  | 'transcription_failed'
  | 'empty_transcript'
  | 'image_failed'
  | 'document_failed'
  | 'link_failed'
  | 'limit_reached';

export interface ConnectionActionFrame {
  type: 'connection_action';
  trace_id: string;
  provider: 'google' | 'microsoft';
  /** reconnect: the grant is the problem; connect: no mailbox on the account, another is needed. */
  action: 'reconnect' | 'connect' | 'permissions';
  /** M5.3: why a reconnect is asked when nothing was refused: drafts need a permission the grant lacks. */
  reason?: 'mail_draft_scope';
}

export interface ScheduleFrame extends ScheduleCard {
  type: 'schedule';
  trace_id: string;
}

export type ServerMessage =
  | { type: 'hello'; person_id: string; connections: number; server_time: string }
  | { type: 'pong'; server_time: string }
  | { type: 'ack'; client_id: string; event: Event }
  | { type: 'typing'; trace_id: string }
  | { type: 'state'; trace_id: string; state: AgentTurnState; label?: string }
  | StepFrame
  | { type: 'delta'; trace_id: string; text: string }
  | { type: 'message'; event: Event }
  | { type: 'reaction'; target_event_id: string; emoji: string; event: Event }
  | ApprovalFrame
  | ApprovalResultFrame
  | ScheduleFrame
  | ConnectionActionFrame
  | { type: 'done'; trace_id: string; timings?: TurnTimings }
  | { type: 'intent'; event_id: string; intent: string; confidence: number; trace_id: string }
  | {
      type: 'error';
      code: ServerErrorCode;
      message: string;
      trace_id?: string;
      attachment_id?: string;
    };

export type ClientMessage =
  | { type: 'ping' }
  /** M5.3: the device's zone and language on every (re)connect. */
  | { type: 'hello'; timezone?: string; language?: string }
  | {
      type: 'message';
      client_id: string;
      text: string;
      language?: string;
      /** M5.3: the device's IANA zone; requests are read and times shown in it. */
      timezone?: string;
      /** Attachments uploaded through POST /media/upload for this message. */
      attachment_ids?: string[];
    };

export type SocketStatus = 'connecting' | 'open' | 'closed' | 'error' | 'unauthenticated';

const MAX_BACKOFF_MS = 10_000;

/**
 * WebSocket client to the PersonAgent Durable Object with automatic reconnect.
 * Authentication is the session cookie; nothing identifying is sent by the client.
 */
export function useAgentSocket(
  enabled: boolean,
  onMessage: (msg: ServerMessage) => void,
  onOpen?: () => void,
) {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const socketRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
  }, [onMessage, onOpen]);

  useEffect(() => {
    if (!enabled) return;
    let attempt = 0;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (disposed) return;
      setStatus('connecting');
      const socket = new WebSocket(wsUrl('/ws'));
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        setStatus('open');
        // M5.3: the device's zone follows the person; the server keeps it for reminders and times.
        try {
          socket.send(
            JSON.stringify({
              type: 'hello',
              timezone: deviceZone(),
              ...(document.documentElement.lang ? { language: document.documentElement.lang } : {}),
            } satisfies ClientMessage),
          );
        } catch {
          // the next message carries the zone too
        }
        // M5.1: the outbox flushes on every (re)connect.
        onOpenRef.current?.();
      };
      socket.onmessage = (ev: MessageEvent<string>) => {
        try {
          onMessageRef.current(JSON.parse(ev.data) as ServerMessage);
        } catch {
          // ignore malformed frames
        }
      };
      socket.onerror = () => setStatus('error');
      socket.onclose = (ev) => {
        socketRef.current = null;
        if (disposed) return;
        if (ev.code === 1008) {
          setStatus('unauthenticated');
          return;
        }
        setStatus('closed');
        const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** attempt++);
        timer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      socketRef.current?.close(1000, 'unmount');
      socketRef.current = null;
    };
  }, [enabled]);

  /** Put a frame on the wire. False when no socket is open: the caller keeps it in the outbox. */
  const send = useCallback((msg: ClientMessage): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }, []);

  return { status, send };
}
